/**
 * Aba lateral do inbox WhatsApp: resumo do paciente vinculado ao contato ou CTA para vincular.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Link as LinkIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  EventRepeat as RescheduleIcon,
  LocalHospital as HospitalIcon,
  Description as DocIcon,
  Medication as MedicationIcon,
  OpenInNew as OpenInNewIcon,
  Warning as WarningIcon,
  Phone as PhoneIcon,
  Refresh as RefreshIcon,
  Videocam as VideocamIcon,
} from '@mui/icons-material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import {
  buildTeleconsultWhatsAppMessage,
  openProfessionalTeleconsultRoom,
  startInboxTeleconsult,
  type InboxAppointmentCandidate,
} from '../../utils/inboxTeleconsultFlow';

interface LinkedPatient {
  id: string;
  full_name: string;
  social_name?: string;
  cpf?: string;
  birth_date?: string;
  phone?: string;
  whatsapp?: string;
  email?: string;
  blood_type?: string;
  allergies?: string[];
  chronic_conditions?: string[];
  insurance_plan?: string;
  last_visit_date?: string;
  total_visits?: number;
  total_spent?: number;
  avatar_url?: string;
  status?: string;
}

interface HealthcareSummaryLite {
  upcoming_appointments?: Array<{
    id: string;
    appointment_date: string;
    start_time?: string;
    title?: string;
    appointment_type?: string;
    status: string;
    clinic_professionals?: { full_name?: string };
  }>;
  stats?: {
    completed_appointments?: number;
    total_budgets?: number;
    total_prescriptions?: number;
  };
  financial?: { total_paid?: number };
}

export interface InboxPatientSidebarProps {
  contactId: string | null;
  contactName?: string;
  contactPhone?: string;
  /** Incrementar após vincular/desvincular no painel de saúde */
  reloadToken?: number;
  onLinkPatient: () => void;
  onOpenHealthcarePanel: (opts?: { tab?: number; schedule?: boolean }) => void;
  /** Envia texto na conversa WhatsApp aberta (obrigatório para enviar link da teleconsulta). */
  onSendMessageToChat?: (content: string) => Promise<void>;
  compact?: boolean;
}

const formatDate = (dateStr?: string) => {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr + 'T12:00:00').toLocaleDateString('pt-BR');
  } catch {
    return '—';
  }
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);

const getAge = (birthDate?: string) => {
  if (!birthDate) return null;
  const today = new Date();
  const birth = new Date(birthDate + 'T12:00:00');
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
};

const aptStatusStyle: Record<string, { label: string; bg: string; color: string }> = {
  scheduled: { label: 'Agendado', bg: '#e8f0fe', color: '#1d4ed8' },
  confirmed: { label: 'Confirmado', bg: '#e6f7f5', color: '#0f766e' },
  waiting: { label: 'Na espera', bg: '#fef6e8', color: '#b45309' },
  in_progress: { label: 'Em atend.', bg: '#f3effc', color: '#6d28d9' },
  attended: { label: 'Atendido', bg: '#e6f7f5', color: '#0d9488' },
  completed: { label: 'Concluído', bg: '#ecfdf3', color: '#15803d' },
  cancelled: { label: 'Cancelado', bg: '#fef2f2', color: '#b91c1c' },
  rescheduled: { label: 'Reagendado', bg: '#fff4ed', color: '#c2410c' },
  no_show: { label: 'Faltou', bg: '#f1f5f9', color: '#64748b' },
};

const InboxPatientSidebar: React.FC<InboxPatientSidebarProps> = ({
  contactId,
  contactName,
  contactPhone,
  reloadToken = 0,
  onLinkPatient,
  onOpenHealthcarePanel,
  onSendMessageToChat,
  compact = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [patient, setPatient] = useState<LinkedPatient | null>(null);
  const [summary, setSummary] = useState<HealthcareSummaryLite | null>(null);
  const [error, setError] = useState('');
  const [teleconsultDialogOpen, setTeleconsultDialogOpen] = useState(false);
  const [teleconsultBusy, setTeleconsultBusy] = useState(false);
  const [teleconsultError, setTeleconsultError] = useState('');
  const [pendingTeleconsult, setPendingTeleconsult] = useState<{
    appointmentId: string;
    patientUrl: string;
  } | null>(null);

  const load = useCallback(async () => {
    if (!contactId) {
      setPatient(null);
      setSummary(null);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { data: linkRes } = await axios.get(
        getApiUrl(`/clinic/healthcare-panel/contact/${contactId}/patient`)
      );
      if (!linkRes?.success || !linkRes?.data) {
        setPatient(null);
        setSummary(null);
        return;
      }
      const p = linkRes.data as LinkedPatient;
      setPatient(p);
      const { data: sumRes } = await axios.get(
        getApiUrl(`/clinic/healthcare-panel/patient/${p.id}/summary`)
      );
      if (sumRes?.success) {
        setSummary(sumRes.data);
      } else {
        setSummary(null);
      }
    } catch {
      setError('Não foi possível carregar o paciente.');
      setPatient(null);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [contactId]);

  useEffect(() => {
    void load();
  }, [load, reloadToken]);

  const upcoming = summary?.upcoming_appointments?.[0];
  const consultCount =
    summary?.stats?.completed_appointments ?? patient?.total_visits ?? 0;
  const totalInvested = summary?.financial?.total_paid ?? patient?.total_spent ?? 0;
  const hasHealthAlerts =
    (patient?.allergies?.length ?? 0) > 0 || (patient?.chronic_conditions?.length ?? 0) > 0;

  const handleOpenTeleconsultDialog = () => {
    if (!patient || !onSendMessageToChat) return;
    setTeleconsultError('');
    setPendingTeleconsult(null);
    setTeleconsultDialogOpen(true);
  };

  const handleTeleconsultChoice = async (sendLinkInChat: boolean) => {
    if (!patient) return;
    setTeleconsultBusy(true);
    setTeleconsultError('');
    try {
      let prepared = pendingTeleconsult;
      if (!prepared) {
        const result = await startInboxTeleconsult({
          patientId: patient.id,
          upcomingAppointments: (summary?.upcoming_appointments ||
            []) as InboxAppointmentCandidate[],
        });
        prepared = {
          appointmentId: result.appointmentId,
          patientUrl: result.patientUrl,
        };
        setPendingTeleconsult(prepared);
      }

      openProfessionalTeleconsultRoom(prepared.appointmentId);

      if (sendLinkInChat && onSendMessageToChat) {
        const message = buildTeleconsultWhatsAppMessage(
          patient.social_name || patient.full_name || contactName || '',
          prepared.patientUrl
        );
        await onSendMessageToChat(message);
      }

      setTeleconsultDialogOpen(false);
      setPendingTeleconsult(null);
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        (e as Error)?.message ||
        'Falha ao concluir a teleconsulta.';
      setTeleconsultError(String(msg));
    } finally {
      setTeleconsultBusy(false);
    }
  };

  const actionBtnSx = {
    textTransform: 'none' as const,
    borderRadius: 2.5,
    fontSize: '0.75rem',
    fontWeight: 600,
    justifyContent: 'flex-start',
    px: 1.5,
  };

  if (!contactId) {
    return (
      <Paper
        elevation={0}
        sx={{
          width: compact ? '100%' : { md: 300, lg: 320 },
          flexShrink: 0,
          borderLeft: 1,
          borderColor: 'divider',
          bgcolor: '#fafbfc',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary" textAlign="center">
          Selecione uma conversa
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        width: compact ? '100%' : { md: 300, lg: 320 },
        maxWidth: compact ? '100%' : { md: 300, lg: 320 },
        flexShrink: 0,
        borderLeft: 1,
        borderColor: 'divider',
        bgcolor: '#fafbfc',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        height: '100%',
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: '1px solid #e8ecf1',
          bgcolor: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#115e59' }}>
          Paciente
        </Typography>
        <Tooltip title="Atualizar">
          <IconButton size="small" onClick={() => void load()} disabled={loading}>
            <RefreshIcon sx={{ fontSize: 18, color: '#0f766e' }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} sx={{ color: '#0f766e' }} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
        ) : !patient ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                bgcolor: '#e6f7f5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <LinkIcon sx={{ fontSize: 28, color: '#0f766e' }} />
            </Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mb: 1 }}>
              Nenhum paciente vinculado
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.5, fontSize: '0.82rem' }}>
              Vincule este contato do WhatsApp a um cadastro de paciente para ver dados clínicos,
              agendar consultas e usar o prontuário sem sair do chat.
            </Typography>
            {contactName && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                Contato: <strong>{contactName}</strong>
                {contactPhone ? ` · ${contactPhone}` : ''}
              </Typography>
            )}
            <Button
              fullWidth
              variant="contained"
              startIcon={<LinkIcon />}
              onClick={onLinkPatient}
              sx={{
                bgcolor: '#0f766e',
                '&:hover': { bgcolor: '#0d9488' },
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 2.5,
                py: 1.1,
              }}
            >
              Vincular paciente
            </Button>
            <Button
              fullWidth
              variant="text"
              size="small"
              onClick={() => onOpenHealthcarePanel()}
              sx={{ mt: 1, textTransform: 'none', color: '#64748b' }}
            >
              Abrir painel de saúde
            </Button>
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
              <Avatar
                src={patient.avatar_url}
                sx={{ width: 48, height: 48, bgcolor: '#0f766e', fontWeight: 700, fontSize: '0.9rem' }}
              >
                {(patient.full_name || 'P').split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()}
              </Avatar>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 800,
                    color: '#0f172a',
                    lineHeight: 1.25,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  {patient.full_name}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                  {patient.birth_date && (
                    <Chip
                      label={`${getAge(patient.birth_date)} anos`}
                      size="small"
                      sx={{ height: 22, fontSize: '0.65rem', bgcolor: '#f1f5f9', borderRadius: '10px' }}
                    />
                  )}
                  <Chip
                    label={patient.status === 'active' ? 'Ativo' : 'Inativo'}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: '0.65rem',
                      bgcolor: patient.status === 'active' ? '#e6f7f5' : '#f1f5f9',
                      color: patient.status === 'active' ? '#0f766e' : '#64748b',
                      borderRadius: '10px',
                    }}
                  />
                </Box>
              </Box>
            </Box>

            {hasHealthAlerts && (
              <Alert
                severity="warning"
                icon={<WarningIcon fontSize="small" />}
                sx={{ mb: 2, borderRadius: 2, py: 0.5, '& .MuiAlert-message': { fontSize: '0.78rem' } }}
              >
                {patient.allergies?.length ? (
                  <Box>
                    <strong>Alergias:</strong> {patient.allergies.join(', ')}
                  </Box>
                ) : null}
                {patient.chronic_conditions?.length ? (
                  <Box sx={{ mt: patient.allergies?.length ? 0.5 : 0 }}>
                    <strong>Condições:</strong> {patient.chronic_conditions.join(', ')}
                  </Box>
                ) : null}
              </Alert>
            )}

            <GridMetrics
              consultas={consultCount}
              orcamentos={summary?.stats?.total_budgets ?? 0}
              receitas={summary?.stats?.total_prescriptions ?? 0}
              investido={formatCurrency(totalInvested)}
            />

            <Divider sx={{ my: 2 }} />

            <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b', letterSpacing: 0.3, mb: 1, display: 'block' }}>
              DADOS RÁPIDOS
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mb: 2 }}>
              {patient.cpf && <InfoRow label="CPF" value={patient.cpf} />}
              {patient.birth_date && (
                <InfoRow label="Nascimento" value={formatDate(patient.birth_date)} />
              )}
              {(patient.whatsapp || patient.phone) && (
                <InfoRow label="Telefone" value={patient.whatsapp || patient.phone || ''} icon={<PhoneIcon sx={{ fontSize: 14 }} />} />
              )}
              {patient.email && <InfoRow label="E-mail" value={patient.email} />}
              {patient.insurance_plan && <InfoRow label="Convênio" value={patient.insurance_plan} />}
              {patient.last_visit_date && (
                <InfoRow label="Última consulta" value={formatDate(patient.last_visit_date)} />
              )}
            </Box>

            {upcoming && (
              <>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b', mb: 1, display: 'block' }}>
                  PRÓXIMA CONSULTA
                </Typography>
                <Card
                  sx={{
                    mb: 2,
                    borderRadius: 2.5,
                    border: 'none',
                    boxShadow: '0 4px 12px rgba(15,23,42,0.06)',
                  }}
                >
                  <CardContent sx={{ py: 1.5, px: 1.75, '&:last-child': { pb: 1.5 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                        {formatDate(upcoming.appointment_date)}
                        {upcoming.start_time ? ` · ${upcoming.start_time.substring(0, 5)}` : ''}
                      </Typography>
                      <Chip
                        label={(aptStatusStyle[upcoming.status] || aptStatusStyle.scheduled).label}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.62rem',
                          fontWeight: 600,
                          bgcolor: (aptStatusStyle[upcoming.status] || aptStatusStyle.scheduled).bg,
                          color: (aptStatusStyle[upcoming.status] || aptStatusStyle.scheduled).color,
                          borderRadius: '8px',
                        }}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {upcoming.title || upcoming.appointment_type || 'Consulta'}
                    </Typography>
                    {upcoming.clinic_professionals?.full_name && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                        {upcoming.clinic_professionals.full_name}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </>
            )}

            <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b', mb: 1, display: 'block' }}>
              AÇÕES
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {onSendMessageToChat && (
                <Button
                  fullWidth
                  variant="contained"
                  size="small"
                  startIcon={<VideocamIcon />}
                  onClick={handleOpenTeleconsultDialog}
                  sx={{
                    ...actionBtnSx,
                    bgcolor: '#1d4ed8',
                    '&:hover': { bgcolor: '#1e40af' },
                  }}
                >
                  Iniciar teleconsulta
                </Button>
              )}
              <Button
                fullWidth
                variant="contained"
                size="small"
                startIcon={<CalendarIcon />}
                onClick={() => onOpenHealthcarePanel({ tab: 1, schedule: true })}
                sx={{ ...actionBtnSx, bgcolor: '#0f766e', '&:hover': { bgcolor: '#0d9488' } }}
              >
                Agendar consulta
              </Button>
              <Button
                fullWidth
                variant="outlined"
                size="small"
                startIcon={<RescheduleIcon />}
                onClick={() => onOpenHealthcarePanel({ tab: 1 })}
                sx={{ ...actionBtnSx, borderColor: '#cbd5e1', color: '#0f766e', bgcolor: '#fff' }}
              >
                Ver agenda / Reagendar
              </Button>
              <Button
                fullWidth
                variant="outlined"
                size="small"
                startIcon={<HospitalIcon />}
                onClick={() => onOpenHealthcarePanel({ tab: 0 })}
                sx={{ ...actionBtnSx, borderColor: '#cbd5e1', color: '#475569', bgcolor: '#fff' }}
              >
                Painel de saúde completo
              </Button>
              <Button
                fullWidth
                variant="outlined"
                size="small"
                startIcon={<OpenInNewIcon />}
                onClick={() => window.open('/agenda', '_blank')}
                sx={{ ...actionBtnSx, borderColor: '#cbd5e1', color: '#475569', bgcolor: '#fff' }}
              >
                Abrir agenda
              </Button>
              <Button
                fullWidth
                variant="outlined"
                size="small"
                startIcon={<DocIcon />}
                onClick={() => window.open(`/clinic/budgets?patientId=${encodeURIComponent(patient.id)}`, '_blank')}
                sx={{ ...actionBtnSx, borderColor: '#cbd5e1', color: '#475569', bgcolor: '#fff' }}
              >
                Orçamento
              </Button>
              <Button
                fullWidth
                variant="outlined"
                size="small"
                startIcon={<MedicationIcon />}
                onClick={() => window.open('/clinic/prescriptions', '_blank')}
                sx={{ ...actionBtnSx, borderColor: '#cbd5e1', color: '#475569', bgcolor: '#fff' }}
              >
                Receituário
              </Button>
            </Box>
          </>
        )}
      </Box>

      <Dialog
        open={teleconsultDialogOpen}
        onClose={() => !teleconsultBusy && setTeleconsultDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Iniciar teleconsulta</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            A sala virtual será preparada e aberta numa nova aba. Deseja enviar o link da
            teleconsulta nesta conversa com{' '}
            <strong>{patient?.social_name || patient?.full_name || contactName}</strong>?
          </Typography>
          {teleconsultError && (
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {teleconsultError}
            </Alert>
          )}
          {teleconsultBusy && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <CircularProgress size={20} sx={{ color: '#1d4ed8' }} />
              <Typography variant="body2" color="text.secondary">
                Preparando sala e link…
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2 }}>
          <Button
            onClick={() => setTeleconsultDialogOpen(false)}
            disabled={teleconsultBusy}
            color="inherit"
          >
            Cancelar
          </Button>
          <Button
            variant="outlined"
            onClick={() => void handleTeleconsultChoice(false)}
            disabled={teleconsultBusy}
          >
            Não enviar
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleTeleconsultChoice(true)}
            disabled={teleconsultBusy || !onSendMessageToChat}
            sx={{ bgcolor: '#1d4ed8', '&:hover': { bgcolor: '#1e40af' } }}
          >
            Sim, enviar no chat
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

function InfoRow({
  label,
  value,
  icon,
}: {
  label: string;
  value: string;
  icon?: React.ReactNode;
}) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {icon}
        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#0f172a' }}>
          {value}
        </Typography>
      </Box>
    </Box>
  );
}

function GridMetrics({
  consultas,
  orcamentos,
  receitas,
  investido,
}: {
  consultas: number;
  orcamentos: number;
  receitas: number;
  investido: string;
}) {
  const items = [
    { label: 'Consultas', value: String(consultas), color: '#0f766e' },
    { label: 'Orçamentos', value: String(orcamentos), color: '#2563eb' },
    { label: 'Receitas', value: String(receitas), color: '#7c3aed' },
    { label: 'Investido', value: investido, color: '#059669', small: true },
  ];
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
      {items.map((k) => (
        <Card
          key={k.label}
          sx={{
            borderRadius: 2.5,
            border: 'none',
            bgcolor: '#fff',
            boxShadow: '0 2px 8px rgba(15,23,42,0.05)',
          }}
        >
          <CardContent sx={{ py: 1.25, px: 1, textAlign: 'center', '&:last-child': { pb: 1.25 } }}>
            <Typography
              variant={k.small ? 'caption' : 'subtitle2'}
              sx={{ fontWeight: 800, color: k.color, fontSize: k.small ? '0.72rem' : undefined, lineHeight: 1.2 }}
            >
              {k.value}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.62rem' }}>
              {k.label}
            </Typography>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
}

export default InboxPatientSidebar;
